import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import axios from "axios";
import { headers } from "../global_vars";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./dashboard-calendar.css";

const localizer = momentLocalizer(moment);

const EVENT_COLORS = {
  project: { backgroundColor: "#3498db", borderColor: "#2980b9" },
  task: { backgroundColor: "#8e44ad", borderColor: "#7d3c98" },
  taxation_project: { backgroundColor: "#16a085", borderColor: "#138d75" },
  document: { backgroundColor: "#e67e22", borderColor: "#d35400" },
  document_critical: { backgroundColor: "#c0392b", borderColor: "#922b21" },
  passport: { backgroundColor: "#d68910", borderColor: "#b9770e" },
  passport_critical: { backgroundColor: "#922b21", borderColor: "#7b241c" },
  appointment: { backgroundColor: "#93ab3c", borderColor: "#7d9432" },
};

function parseLocalDay(dateStr) {
  const [y, m, d] = String(dateStr).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
}

function parseEvent(e) {
  let style = EVENT_COLORS[e.eventType] || EVENT_COLORS.project;
  if (e.eventType === "document" && e.urgency === "critical") {
    style = EVENT_COLORS.document_critical;
  } else if (e.eventType === "document" && e.urgency === "warning") {
    style = EVENT_COLORS.document;
  } else if (e.eventType === "passport" && e.urgency === "critical") {
    style = EVENT_COLORS.passport_critical;
  } else if (e.eventType === "passport" && e.urgency === "warning") {
    style = EVENT_COLORS.passport;
  }

  if (e.allDay === false) {
    const start = new Date(e.start);
    const end = e.end ? new Date(e.end) : moment(start).add(1, "hour").toDate();
    return {
      id: e.id,
      title: e.title,
      start,
      end,
      allDay: false,
      resource: e,
      style,
    };
  }

  // react-big-calendar treats all-day end as exclusive (must be the day after start)
  const start = moment(parseLocalDay(e.start)).startOf("day").toDate();
  const end = moment(parseLocalDay(e.start)).startOf("day").add(1, "day").toDate();
  return {
    id: e.id,
    title: e.title,
    start,
    end,
    allDay: true,
    resource: e,
    style,
  };
}

export default function DashboardCalendar() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const fetchSeqRef = useRef(0);

  const fetchEvents = useCallback(async (rangeStart, rangeEnd) => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const start = moment(rangeStart).format("YYYY-MM-DD");
      const end = moment(rangeEnd).format("YYYY-MM-DD");
      const res = await axios.get("https://ultima.icsgr.com/api/dashboard/calendar-events/", {
        headers: { ...headers, Authorization: "Token " + localStorage.getItem("userToken") },
        params: { start, end },
      });
      if (seq !== fetchSeqRef.current) return;
      const raw = res?.data?.data?.events || [];
      setEvents(raw.map(parseEvent));
    } catch (_e) {
      if (seq === fetchSeqRef.current) setEvents([]);
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, []);

  const loadRangeForDate = useCallback((date) => {
    const start = moment(date).startOf("month").subtract(7, "days").toDate();
    const end = moment(date).endOf("month").add(7, "days").toDate();
    fetchEvents(start, end);
  }, [fetchEvents]);

  useEffect(() => {
    loadRangeForDate(currentDate);
  }, [currentDate, loadRangeForDate]);

  const onNavigate = useCallback((date) => {
    setCurrentDate(date);
  }, []);

  const onSelectEvent = useCallback((event) => {
    const url = event?.resource?.url;
    if (url) window.location.href = url;
  }, []);

  const eventStyleGetter = useCallback((event) => ({
    style: {
      ...event.style,
      color: "#fff",
      borderRadius: 4,
      border: "none",
      fontSize: 12,
    },
  }), []);

  const legend = useMemo(
    () => (
      <div className="dashboard-calendar-legend">
        <span><i style={{ background: EVENT_COLORS.project.backgroundColor }} /> Project deadline</span>
        <span><i style={{ background: EVENT_COLORS.task.backgroundColor }} /> Task deadline</span>
        <span><i style={{ background: EVENT_COLORS.taxation_project.backgroundColor }} /> Taxation deadline</span>
        <span><i style={{ background: EVENT_COLORS.document.backgroundColor }} /> Document expiring (30d)</span>
        <span><i style={{ background: EVENT_COLORS.document_critical.backgroundColor }} /> Document expiring (7d)</span>
        <span><i style={{ background: EVENT_COLORS.passport.backgroundColor }} /> Passport expiring</span>
        <span><i style={{ background: EVENT_COLORS.appointment.backgroundColor }} /> Appointment</span>
      </div>
    ),
    []
  );

  return (
    <div className="dashboard-calendar-wrap">
      {legend}
      {loading ? <div className="mb-2 text-muted">Loading calendar…</div> : null}
      <Calendar
        localizer={localizer}
        events={events}
        date={currentDate}
        startAccessor="start"
        endAccessor="end"
        formats={{
          dateFormat: "DD/MM/YYYY",
          dayFormat: "DD/MM",
          weekdayFormat: "ddd",
          monthHeaderFormat: "MMMM YYYY",
          dayHeaderFormat: "dddd DD/MM",
          dayRangeHeaderFormat: ({ start, end }, culture, loc) =>
            `${loc.format(start, "DD/MM/YYYY", culture)} – ${loc.format(end, "DD/MM/YYYY", culture)}`,
          agendaDateFormat: "DD/MM/YYYY",
          agendaHeaderFormat: ({ start, end }, culture, loc) =>
            `${loc.format(start, "DD/MM/YYYY", culture)} – ${loc.format(end, "DD/MM/YYYY", culture)}`,
        }}
        style={{ height: 560 }}
        onNavigate={onNavigate}
        onSelectEvent={onSelectEvent}
        eventPropGetter={eventStyleGetter}
        popup
        views={["month", "week", "agenda"]}
        defaultView="month"
      />
    </div>
  );
}
